import { useMemo, useState } from 'react'

import {
  SanityPrintAreaType,
  SanityPrintFeeCombination,
  SanityPrintPermission,
  SanityPrintType,
} from '@data/sanity/queries/types/print'
import { SanityShopPrint } from '@data/sanity/queries/types/shop'
import { SanityPrintConfiguratorStrings } from '@data/sanity/queries/types/strings'
import { getPrintFeePrice } from '@lib/print/fee'
import { SlotConfiguration, SlotWithConfiguration } from '@lib/print/types'

import PrintField from './print-field'

interface PrintConfiguratorSlotImageFieldProps {
  printConfiguratorStrings: SanityPrintConfiguratorStrings
  slotsWithConfiguration: SlotWithConfiguration[]
  printFeeCombinations: SanityPrintFeeCombination[]
  activeSlotIndex: number
  imageAreaTypes: SanityPrintAreaType[]
  activePrints: SanityShopPrint[]
  permissions?: SanityPrintPermission | null
  onConfigurationChange: (
    slotId: string,
    newConfiguration: Partial<SlotConfiguration>,
  ) => void
}

const PrintConfiguratorSlotImageField = ({
  printConfiguratorStrings,
  slotsWithConfiguration,
  printFeeCombinations,
  activeSlotIndex,
  imageAreaTypes,
  activePrints,
  permissions,
  onConfigurationChange,
}: PrintConfiguratorSlotImageFieldProps) => {
  const hasLogoPermission = !!permissions?.logo
  const hasImagePermission = !!permissions?.image
  const hasLogoArea = imageAreaTypes.includes(SanityPrintAreaType.LOGO)
  const hasImageArea = imageAreaTypes.includes(SanityPrintAreaType.IMAGE)
  const hasLogoPrints = activePrints.some((print) => print.type === 'logo')
  const hasImagePrints = activePrints.some((print) => print.type === 'image')
  const showLogos = hasLogoPermission && hasLogoArea && hasLogoPrints
  const showImages = hasImagePermission && hasImageArea && hasImagePrints

  const [printAssetType, setPrintAssetType] = useState<SanityPrintType>(
    showLogos ? 'logo' : 'image',
  )

  const activeSlot = useMemo(() => {
    return slotsWithConfiguration[activeSlotIndex]
  }, [activeSlotIndex, slotsWithConfiguration])

  // Determine the price based on whether selecting this print type will increase the price (add print fee)
  const logoPrintFeePrice = useMemo(() => {
    return getPrintFeePrice(
      printFeeCombinations,
      slotsWithConfiguration,
      activeSlotIndex,
      'logo',
    )
  }, [activeSlotIndex, printFeeCombinations, slotsWithConfiguration])

  const imagePrintFeePrice = useMemo(() => {
    return getPrintFeePrice(
      printFeeCombinations,
      slotsWithConfiguration,
      activeSlotIndex,
      'image',
    )
  }, [activeSlotIndex, printFeeCombinations, slotsWithConfiguration])

  const handlePrintAssetTypeChange = (printType: SanityPrintType) => {
    setPrintAssetType(printType)

    onConfigurationChange(activeSlot._id, {
      logo: null,
      image: null,
    })
  }

  return (
    <div>
      {/* United image print (combined row for logo & image) */}
      {!permissions?.separateLogoImage && (
        <PrintField
          fieldEnabledStatus={activeSlot.configuration.imageEnabled}
          fieldValue={
            activeSlot.configuration.image ?? activeSlot.configuration.logo
          }
          prints={activePrints}
          printAssetType={printAssetType}
          printConfiguratorStrings={printConfiguratorStrings}
          showLogoPrints={imageAreaTypes.includes(SanityPrintAreaType.LOGO)}
          showImagePrints={imageAreaTypes.includes(SanityPrintAreaType.IMAGE)}
          logoPrintFeePrice={logoPrintFeePrice}
          imagePrintFeePrice={imagePrintFeePrice}
          onPrintAssetTypeChange={handlePrintAssetTypeChange}
          onPrintClick={(print) => {
            onConfigurationChange(activeSlot._id, {
              logo: print?.type === 'logo' ? print : null,
              image: print?.type === 'image' ? print : null,
            })
          }}
          onEnabledStatusChange={(newStatus) => {
            onConfigurationChange(activeSlot._id, {
              logoEnabled: newStatus,
              imageEnabled: newStatus,
            })
          }}
        />
      )}

      {/* Separate image print (individual rows for logo & image) */}
      {!!permissions?.separateLogoImage && (
        <>
          {showLogos && (
            <PrintField
              fieldEnabledStatus={activeSlot.configuration.logoEnabled}
              fieldValue={activeSlot.configuration.logo}
              prints={activePrints}
              printAssetType="logo"
              printConfiguratorStrings={printConfiguratorStrings}
              logoPrintFeePrice={logoPrintFeePrice}
              imagePrintFeePrice={imagePrintFeePrice}
              onPrintClick={(print) => {
                onConfigurationChange(activeSlot._id, {
                  logo: print,
                })
              }}
              onEnabledStatusChange={(newStatus) => {
                onConfigurationChange(activeSlot._id, {
                  logoEnabled: newStatus,
                })
              }}
            />
          )}

          {showImages && (
            <PrintField
              fieldEnabledStatus={activeSlot.configuration.imageEnabled}
              fieldValue={activeSlot.configuration.image}
              prints={activePrints}
              printAssetType="image"
              printConfiguratorStrings={printConfiguratorStrings}
              logoPrintFeePrice={logoPrintFeePrice}
              imagePrintFeePrice={imagePrintFeePrice}
              onPrintClick={(print) => {
                onConfigurationChange(activeSlot._id, {
                  image: print,
                })
              }}
              onEnabledStatusChange={(newStatus) => {
                onConfigurationChange(activeSlot._id, {
                  imageEnabled: newStatus,
                })
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

export default PrintConfiguratorSlotImageField
